import React, { useState, useMemo, useCallback } from "react";
import { Heading } from "../../design/heading";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../design/table";
import { useChild } from "../../contexts/ChildContext";
import { Avatar } from "../../design/avatar";
import { useCase } from "../../contexts/CaseContext";
import { getInitials } from "../../utils/string";
import LoadingView from "../../components/LoadingView";
import ErrorView from "../../components/ErrorView";
import { Input } from "../../design/input";
import { Button } from "../../design/button";
import { Select } from "../../design/select";
import { XMarkIcon, FunnelIcon } from "@heroicons/react/20/solid";
import EmptyStateView from "../../components/EmptyStateView";
import ViewToggle, { ViewMode } from "../../components/ViewToggle";
import ChildGrid from "./components/ChildGrid";
import {
  RACE_LABELS,
  SEX_LABELS,
  LANGUAGE_LABELS,
  SPECIAL_NEEDS_LABELS,
  SpecialNeed,
} from "@visitproof/types";

const ChildListPage: React.FC = () => {
  const { cases } = useCase();
  const { childList, loading, error } = useChild();
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const [showFilters, setShowFilters] = useState(false);

  // Demographic filters
  const [raceFilter, setRaceFilter] = useState("");
  const [sexFilter, setSexFilter] = useState("");
  const [languageFilter, setLanguageFilter] = useState("");
  const [specialNeedsFilter, setSpecialNeedsFilter] = useState("");
  const [hasSpecialNeedsFilter, setHasSpecialNeedsFilter] = useState("");

  // Memoized filtered children
  const filteredChildren = useMemo(() => {
    return childList.filter((child) => {
      // Text search
      if (searchTerm.trim()) {
        const searchLower = searchTerm.toLowerCase();
        const fullName = `${child.firstName} ${child.lastName}`.toLowerCase();
        const caseIdentifier = cases.find((c) => c.id === child.caseId)?.identifier?.toLowerCase() || "";
        if (!fullName.includes(searchLower) && !caseIdentifier.includes(searchLower)) {
          return false;
        }
      }

      // Demographic filters
      if (raceFilter && child.race !== raceFilter) return false;
      if (sexFilter && child.sex !== sexFilter) return false;
      if (languageFilter && child.primaryLanguage !== languageFilter) return false;
      if (hasSpecialNeedsFilter === "yes" && !child.hasSpecialNeeds) return false;
      if (hasSpecialNeedsFilter === "no" && child.hasSpecialNeeds) return false;
      if (specialNeedsFilter && (!child.specialNeeds || !child.specialNeeds.includes(specialNeedsFilter as SpecialNeed))) return false;

      return true;
    });
  }, [childList, searchTerm, cases, raceFilter, sexFilter, languageFilter, specialNeedsFilter, hasSpecialNeedsFilter]);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  }, []);

  const activeFilterCount = [raceFilter, sexFilter, languageFilter, specialNeedsFilter, hasSpecialNeedsFilter].filter(Boolean).length;

  const hasActiveFilters = Boolean(searchTerm.trim()) || activeFilterCount > 0;

  const clearFilters = useCallback(() => {
    setSearchTerm("");
    setRaceFilter("");
    setSexFilter("");
    setLanguageFilter("");
    setSpecialNeedsFilter("");
    setHasSpecialNeedsFilter("");
  }, []);

  if (loading.list) {
    return <LoadingView />;
  }

  if (error.list) {
    return <ErrorView error={error.list} />;
  }

  const hasChildren = childList.length > 0;
  const hasFilteredChildren = filteredChildren.length > 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <Heading>Children</Heading>
          {hasChildren && (
            <p className="text-sm text-zinc-500 dark:text-zinc-400">
              {filteredChildren.length} of {childList.length} children
              {hasActiveFilters && " (filtered)"}
            </p>
          )}
        </div>
        {hasChildren && (
          <ViewToggle view={viewMode} onViewChange={setViewMode} />
        )}
      </div>

      {/* Search & Filters */}
      {hasChildren && (
        <div className="space-y-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative flex-1 sm:max-w-sm">
              <Input
                type="search"
                placeholder="Search children..."
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </div>

            <div className="flex items-center gap-2">
              <Button
                plain
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2 text-zinc-600 hover:text-zinc-800 dark:text-zinc-400 dark:hover:text-zinc-200"
              >
                <FunnelIcon className="w-4 h-4" />
                Filters
                {activeFilterCount > 0 && (
                  <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-medium text-white bg-blue-600 rounded-full">
                    {activeFilterCount}
                  </span>
                )}
              </Button>

              {hasActiveFilters && (
                <Button
                  plain
                  onClick={clearFilters}
                  className="flex items-center gap-2 text-zinc-600 hover:text-zinc-800 dark:text-zinc-400 dark:hover:text-zinc-200"
                >
                  <XMarkIcon className="w-4 h-4" />
                  Clear filters
                </Button>
              )}
            </div>
          </div>

          {/* Demographic Filters */}
          {showFilters && (
            <div className="p-4 border rounded-lg border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800/50">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                <div>
                  <label className="block mb-1 text-xs font-medium text-zinc-500 dark:text-zinc-400">Race / Ethnicity</label>
                  <Select value={raceFilter} onChange={(e) => setRaceFilter(e.target.value)}>
                    <option value="">All</option>
                    {Object.entries(RACE_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </Select>
                </div>
                <div>
                  <label className="block mb-1 text-xs font-medium text-zinc-500 dark:text-zinc-400">Sex</label>
                  <Select value={sexFilter} onChange={(e) => setSexFilter(e.target.value)}>
                    <option value="">All</option>
                    {Object.entries(SEX_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </Select>
                </div>
                <div>
                  <label className="block mb-1 text-xs font-medium text-zinc-500 dark:text-zinc-400">Primary Language</label>
                  <Select value={languageFilter} onChange={(e) => setLanguageFilter(e.target.value)}>
                    <option value="">All</option>
                    {Object.entries(LANGUAGE_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </Select>
                </div>
                <div>
                  <label className="block mb-1 text-xs font-medium text-zinc-500 dark:text-zinc-400">Has Special Needs</label>
                  <Select value={hasSpecialNeedsFilter} onChange={(e) => setHasSpecialNeedsFilter(e.target.value)}>
                    <option value="">All</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </Select>
                </div>
                <div>
                  <label className="block mb-1 text-xs font-medium text-zinc-500 dark:text-zinc-400">Special Need Type</label>
                  <Select value={specialNeedsFilter} onChange={(e) => setSpecialNeedsFilter(e.target.value)}>
                    <option value="">All</option>
                    {Object.entries(SPECIAL_NEEDS_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </Select>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Content */}
      {!hasChildren ? (
        <EmptyStateView title="No Children Found" message="Please create a case first" />
      ) : !hasFilteredChildren ? (
        <div className="mt-8 text-center">
          <Heading>No children match your search</Heading>
          <p className="mt-2 text-zinc-500 dark:text-zinc-400">
            Try adjusting your search terms or{" "}
            <button
              onClick={clearFilters}
              className="text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300"
            >
              clear all filters
            </button>
          </p>
        </div>
      ) : viewMode === "grid" ? (
        <ChildGrid children={filteredChildren} cases={cases} />
      ) : (
        <Table className="[--gutter:theme(spacing.6)] lg:[--gutter:theme(spacing.10)]">
          <TableHead>
            <TableRow>
              <TableHeader>Name</TableHeader>
              <TableHeader>Case</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredChildren.map((child) => (
              <TableRow key={child.id} href={`/child/${child.id}`}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Avatar
                      src={child.avatarUrl}
                      className="size-6"
                      initials={getInitials(
                        `${child.firstName} ${child.lastName}`,
                      )}
                    />
                    <span>{`${child.firstName} ${child.lastName}`}</span>
                  </div>
                </TableCell>
                <TableCell>
                  {cases.find((c) => c.id === child.caseId)?.identifier ??
                    child.caseId}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
};

export default ChildListPage;
